// Core simulation — processes monthly repayments, defaults, and bank state

import { getAggregateEffects } from './economyEngine';

// Calculate monthly payment for an amortizing loan
export function calculateMonthlyPayment(principal, annualRate, durationMonths) {
  const r = annualRate / 12;
  if (r === 0) return principal / durationMonths;
  return (principal * r * Math.pow(1 + r, durationMonths)) / (Math.pow(1 + r, durationMonths) - 1);
}

// Determine if a loan defaults this month
export function checkDefault(loan, activeEvents, month) {
  const effects = getAggregateEffects(activeEvents);

  let baseRisk = loan.trueRisk;

  // Player rate impact: higher rate = harder to repay = more defaults
  const rateDelta = loan.playerRate - loan.suggestedRate;
  if (rateDelta > 0.05) baseRisk *= 1.3;
  else if (rateDelta > 0.03) baseRisk *= 1.15;
  else if (rateDelta < -0.02) baseRisk *= 0.9;

  // Apply global default modifier
  baseRisk *= effects.defaultMod;

  // Apply sector modifier
  const sectorMod = effects.sectorDefaultMods[loan.borrowerType] || 1.0;
  baseRisk *= sectorMod;

  // Loans are more likely to default early on (months 2-6)
  const monthsActive = month - loan.startMonth;
  if (monthsActive <= 6) baseRisk *= 1.3;
  else if (monthsActive <= 12) baseRisk *= 1.1;
  else if (monthsActive > 24) baseRisk *= 0.8;

  // Monthly default probability (convert annual-ish to monthly)
  const monthlyDefaultProb = 1 - Math.pow(1 - baseRisk, 1 / 12);

  return Math.random() < monthlyDefaultProb;
}

// Process one month of active loans
export function simulateMonth(activeLoans, activeEvents, month, bankCapital) {
  const results = {
    totalRepayments: 0,
    totalInterestIncome: 0,
    totalPrincipalRepaid: 0,
    defaults: [],
    completedLoans: [],
    defaultLosses: 0,
    updatedLoans: [],
  };

  for (const loan of activeLoans) {
    // Check for default
    if (checkDefault(loan, activeEvents, month)) {
      // Default! Recover some of the remaining principal (recovery rate)
      const recoveryRate = loan.creditScore > 700 ? 0.5
        : loan.creditScore > 600 ? 0.35
        : loan.creditScore > 500 ? 0.2
        : 0.1;

      const loss = loan.remainingPrincipal * (1 - recoveryRate);
      const recovered = loan.remainingPrincipal * recoveryRate;

      results.defaults.push({
        ...loan,
        loss,
        recovered,
        defaultMonth: month,
      });
      results.defaultLosses += loss;
      results.totalRepayments += recovered;
      continue;
    }

    // Normal repayment
    const monthlyPayment = calculateMonthlyPayment(
      loan.originalAmount,
      loan.playerRate,
      loan.duration
    );

    const interestPortion = loan.remainingPrincipal * (loan.playerRate / 12);
    const principalPortion = Math.min(monthlyPayment - interestPortion, loan.remainingPrincipal);

    const newRemaining = Math.max(0, loan.remainingPrincipal - principalPortion);

    results.totalInterestIncome += interestPortion;
    results.totalPrincipalRepaid += principalPortion;
    results.totalRepayments += monthlyPayment;

    if (newRemaining <= 1) {
      // Loan fully repaid
      results.completedLoans.push({ ...loan, completedMonth: month });
    } else {
      results.updatedLoans.push({
        ...loan,
        remainingPrincipal: newRemaining,
        monthsRemaining: loan.monthsRemaining - 1,
        totalRepaid: (loan.totalRepaid || 0) + monthlyPayment,
      });
    }
  }

  return results;
}

// Calculate portfolio risk score (0-100)
export function calculatePortfolioRisk(activeLoans, bankCapital) {
  if (activeLoans.length === 0) return 0;

  let totalExposure = 0;
  let weightedRisk = 0;
  let concentrationRisk = 0;

  const typeExposure = { individual: 0, business: 0, startup: 0 };

  for (const loan of activeLoans) {
    const exposure = loan.remainingPrincipal;
    totalExposure += exposure;
    weightedRisk += loan.trueRisk * exposure;
    typeExposure[loan.borrowerType] = (typeExposure[loan.borrowerType] || 0) + exposure;
  }

  if (totalExposure === 0) return 0;

  // Weighted average risk
  const avgRisk = weightedRisk / totalExposure;

  // Leverage ratio (exposure vs capital)
  const leverageRatio = totalExposure / Math.max(1, bankCapital);

  // Concentration: penalize heavy concentration in one sector
  for (const exposure of Object.values(typeExposure)) {
    const pct = exposure / totalExposure;
    if (pct > 0.7) concentrationRisk += 15;
    else if (pct > 0.5) concentrationRisk += 8;
  }

  // Combine factors
  const riskScore = (avgRisk * 150) + (leverageRatio * 15) + concentrationRisk;

  return Math.max(0, Math.min(100, Math.round(riskScore)));
}

// Check bank health
export function checkBankHealth(liquidity, totalCapital, initialCapital) {
  if (liquidity < 0) return 'bankrupt';
  if (totalCapital < initialCapital * 0.1) return 'bankrupt';
  if (liquidity < totalCapital * 0.05) return 'critical';
  if (liquidity < totalCapital * 0.15) return 'warning';
  return 'healthy';
}

// Calculate bank reputation based on performance
export function calculateReputation(monthlyHistory) {
  if (monthlyHistory.length === 0) return 50;

  let rep = 50;
  const recent = monthlyHistory.slice(-12);

  for (const month of recent) {
    if (month.defaults === 0) rep += 2;
    if (month.profit > 0) rep += 1;
    if (month.defaults > 2) rep -= 5;
    if (month.profit < 0) rep -= 3;
  }

  return Math.max(0, Math.min(100, rep));
}
